import { type RefObject, useCallback, useEffect, useRef } from "react";

interface UseOutsideDismissOptions {
	readonly isOpen: boolean;
	readonly wrapperRef: RefObject<HTMLElement | null>;
	readonly onDismiss: () => void;
}

/**
 * Closes a panel/popover on outside pointerdown or Escape, suppressing the
 * subsequent click so it never reaches the underlying UI (e.g. product links).
 *
 * Uses capture-phase listeners on `document` so the events are intercepted
 * before they propagate to any target element.
 */
export function useOutsideDismiss({
	isOpen,
	wrapperRef,
	onDismiss,
}: UseOutsideDismissOptions): void {
	const clickGuardRef = useRef<((event: MouseEvent) => void) | null>(null);
	const onDismissRef = useRef(onDismiss);
	useEffect(() => {
		onDismissRef.current = onDismiss;
	}, [onDismiss]);

	const dismiss = useCallback(() => {
		onDismissRef.current();
	}, []);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handleClickOutside = (event: PointerEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				event.stopPropagation();
				event.preventDefault();
				const suppressNextClick = (clickEvent: MouseEvent) => {
					clickEvent.stopPropagation();
					clickEvent.preventDefault();
					document.removeEventListener("click", suppressNextClick, true);
					clickGuardRef.current = null;
				};
				clickGuardRef.current = suppressNextClick;
				document.addEventListener("click", suppressNextClick, true);
				dismiss();
			}
		};
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				dismiss();
			}
		};
		document.addEventListener("pointerdown", handleClickOutside, true);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("pointerdown", handleClickOutside, true);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen, wrapperRef, dismiss]);

	useEffect(() => {
		return () => {
			if (clickGuardRef.current) {
				document.removeEventListener("click", clickGuardRef.current, true);
				clickGuardRef.current = null;
			}
		};
	}, []);
}
